import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useForm } from "react-hook-form";

import {
  DocumentGroupEnumType,
  DocumentTypeEnumType,
} from "@libs/api/patients";
import {
  DocumentCard,
  DocumentCardLoadingSkeleton,
} from "@/blocks/DocumentCard";
import { DocumentTypeHints } from "@/blocks/DocumentTypeHints";
import { DocumentTypeSelectDialog } from "@/blocks/DocumentTypeSelectDialog";
import { SecureTextEntryToggle } from "@/blocks/SecureTextEntryToggle";
import { UploadButton } from "@/blocks/UploadButton";
import {
  VnAlertDialog,
  VnAlertDialogAction,
  VnAlertDialogCancel,
  VnAlertDialogContent,
  VnAlertDialogDescription,
  VnAlertDialogFooter,
  VnAlertDialogHeader,
  VnAlertDialogTitle,
} from "@/components/VnAlertDialog";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnForm } from "@/components/VnForm";
import { VnScrollView } from "@/components/VnScrollView";
import { VnSheetToastContainer } from "@/components/VnSheetToast";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useDeleteDocumentMutation } from "@/data/mutations/uploadedDocumentsMutations";
import { useGetHouseholdMemberQuery } from "@/data/queries/householdMembersQueries";
import {
  useListDocumentCategoriesQuery,
  useListDocumentRequirementsQuery,
  useListDocumentsQuery,
} from "@/data/queries/uploadedDocumentsQueries";
import {
  coverageCheckFormSchema,
  CoverageCheckFormType,
} from "@/domain/householdMember";
import {
  documentTypes,
  labelForDocumentGroup,
  ListDocumentsFilterType,
} from "@/domain/uploadedDocuments";
import { useFilePicker } from "@/hooks/useFilePicker";
import { useSheetToast } from "@/hooks/useSheetToast";
import { useTranslator } from "@/hooks/useTranslate";
import { useUploadDocument } from "@/hooks/useUploadDocument";
import { ssnMask } from "@/utils/string";

const IMMIGRATION_GROUP: DocumentGroupEnumType = "PROOF_OF_CITIZENSHIP";
const SSN_GROUP: DocumentGroupEnumType = "SOCIAL_SECURITY_NUMBER";
const RESIDENCE_GROUP: DocumentGroupEnumType = "PROOF_OF_RESIDENCE";
const BIRTH_OR_PASSPORT_TYPES: DocumentTypeEnumType[] = [
  "BIRTH_CERTIFICATE",
  "PASSPORT",
];

type DocumentsSheetPropsType = {
  householdMemberId: string;
  documentGroup: DocumentGroupEnumType;
  context?: "application" | "profile";
};

export const DocumentsSheet: FC<DocumentsSheetPropsType> = ({
  householdMemberId,
  documentGroup,
  context = "application",
}) => {
  const { t } = useTranslator();
  const sheetToast = useSheetToast();
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<DocumentTypeEnumType | null>(null);
  const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false);
  const [documentIdToDelete, setDocumentIdToDelete] = useState<string | null>(
    null,
  );

  const { data: documentCategories } = useListDocumentCategoriesQuery();

  const queryFilter: ListDocumentsFilterType = useMemo(
    () => ({
      documentGroup,
      householdMemberId,
    }),
    [documentGroup, householdMemberId],
  );

  const { data: documents, isLoading } = useListDocumentsQuery(queryFilter);

  const { data: householdMember } =
    useGetHouseholdMemberQuery(householdMemberId);
  const { data: requirements } = useListDocumentRequirementsQuery();

  const memberRequirements = requirements?.find(
    member => member.householdMemberId === householdMemberId,
  );
  const currentSection = memberRequirements?.sections.find(
    section => section.group === documentGroup,
  );

  const [isSsnVisible, setIsSsnVisible] = useState(false);
  const [isImmigrationPromptOpen, setIsImmigrationPromptOpen] = useState(false);

  const { mutate: updateHouseholdMember, isPending: isSavingSsn } =
    useUpdateHouseholdMemberMutation();

  const ssnForm = useForm<CoverageCheckFormType>({
    defaultValues: { ssn: "", medicaidSubscriberId: "" },
    resolver: standardSchemaResolver(coverageCheckFormSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (householdMember?.ssn) {
      ssnForm.reset({ ssn: householdMember.ssn, medicaidSubscriberId: "" });
    }
  }, [householdMember?.ssn, ssnForm]);

  const { pendingUploads, uploadDocuments, isUploading } = useUploadDocument({
    householdMemberId,
  });

  // Cross-section credit: when a document the user just uploaded here also
  // completes a *different* applicable section (e.g. a USCIS ID in Identity
  // also completing Immigration), confirm it. We snapshot which sections were
  // already complete at upload time (see handleDocumentTypeSelect) and diff the
  // first post-upload requirements refresh against it.
  const completedBeforeUploadRef = useRef<Set<DocumentGroupEnumType> | null>(
    null,
  );

  useEffect(() => {
    const completedBeforeUpload = completedBeforeUploadRef.current;
    // Wait until the upload (scan/polling) has finished so a refetch mid-upload
    // can't consume the snapshot early. `isUploading` is an intentional guard,
    // NOT a dependency: it flips false right before requirements is invalidated,
    // so we want to evaluate only when that resulting refresh lands.
    if (isUploading || !memberRequirements || completedBeforeUpload === null) {
      return;
    }

    // Consume on this first post-upload refresh regardless of the outcome, so a
    // no-cross-credit upload can't leave the snapshot set and later mis-fire on
    // an unrelated requirements change.
    completedBeforeUploadRef.current = null;

    const newlyCompletedOtherSection = memberRequirements.sections.find(
      section =>
        section.group !== documentGroup &&
        // Profile shows every section (non-applicable ones as "Optional"), so the
        // cross-credit toast should mention them there too; the application flow
        // hides non-applicable sections, so it shouldn't
        // but only applicable ones in the application flow.
        (context === "profile" || section.isApplicable) &&
        section.isComplete &&
        !completedBeforeUpload.has(section.group),
    );

    if (newlyCompletedOtherSection) {
      sheetToast.success(
        t("native:applicantDocuments.alsoCompleted", {
          section: t(labelForDocumentGroup(newlyCompletedOtherSection.group)),
        }),
      );
    }
    // isUploading is a guard only (see above); re-run on requirements change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberRequirements, documentGroup]);

  // Cross-member residence credit: a residence document the user just uploaded
  // here can also complete *other* household members' Proof of Residence (they
  // share the same home address). Snapshot which other members were already
  // complete at upload time (see handleDocumentTypeSelect) and, once the upload
  // finishes, confirm it if this upload newly completed any of them — same
  // guards/pattern as the cross-section toast above.
  const residenceCompleteMembersBeforeRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    const residenceCompleteBefore = residenceCompleteMembersBeforeRef.current;
    if (isUploading || !requirements || residenceCompleteBefore === null) {
      return;
    }

    residenceCompleteMembersBeforeRef.current = null;

    const hasCompletedAnotherMember = requirements.some(
      member =>
        member.householdMemberId !== householdMemberId &&
        member.sections.find(section => section.group === RESIDENCE_GROUP)
          ?.isComplete &&
        !residenceCompleteBefore.has(member.householdMemberId),
    );

    if (!hasCompletedAnotherMember) return;

    sheetToast.success(
      t("native:applicantDocuments.alsoCompletedResidenceForMembers", {
        section: t(labelForDocumentGroup(RESIDENCE_GROUP)),
      }),
    );
    // isUploading is a guard only; re-run on requirements change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirements, householdMemberId]);

  const { pickFiles } = useFilePicker();

  const getDocumentCategoryId = useCallback(
    (docType: DocumentTypeEnumType): string | null => {
      if (!documentCategories) return null;

      const category = documentCategories.find(
        cat => cat.type === docType && cat.group === documentGroup,
      );

      return category?.id ?? null;
    },
    [documentCategories, documentGroup],
  );

  const { mutate: deleteDocument, isPending: isDeletingDocument } =
    useDeleteDocumentMutation({
      householdMemberId,
      documentGroup,
    });

  const documentTypeOptions = useMemo(() => {
    if (!documentCategories) return [];

    const documentCategoriesByGroup = documentCategories.filter(
      category => category.group === documentGroup,
    );

    return documentCategoriesByGroup.flatMap(
      category =>
        documentTypes.find(type => type.value === category.type) ?? [],
    );
  }, [documentCategories, documentGroup]);

  const handleDocumentTypeSelect = useCallback(async () => {
    setIsTypeDialogOpen(false);

    if (!selectedDocumentType) return;

    const categoryId = getDocumentCategoryId(selectedDocumentType);
    if (!categoryId) return;

    const result = await pickFiles();

    if (result.isSuccess && result.files.length > 0) {
      // Snapshot which sections are already complete so the effect above can
      // detect if this upload also completes another one (cross-section credit).
      completedBeforeUploadRef.current = new Set(
        (memberRequirements?.sections ?? [])
          .filter(section => section.isComplete)
          .map(section => section.group),
      );

      // For a residence upload, also snapshot which *other* household members
      // already had Proof of Residence complete, so the effect can tell whether
      // this upload newly completes any of them via shared-address credit
      // (vs. ones that were already complete before).
      if (documentGroup === RESIDENCE_GROUP) {
        residenceCompleteMembersBeforeRef.current = new Set(
          (requirements ?? [])
            .filter(member => member.householdMemberId !== householdMemberId)
            .filter(
              member =>
                member.sections.find(
                  section => section.group === RESIDENCE_GROUP,
                )?.isComplete,
            )
            .map(member => member.householdMemberId),
        );
      }

      void uploadDocuments({
        files: result.files,
        documentCategoryId: categoryId,
        documentType: selectedDocumentType,
      });

      // A satisfactory-immigration-status member needs more than a birth
      // certificate/passport to complete Proof of Immigration.
      if (
        documentGroup === IMMIGRATION_GROUP &&
        householdMember?.citizenshipStatus === "IMMIGRANT" &&
        BIRTH_OR_PASSPORT_TYPES.includes(selectedDocumentType) &&
        !currentSection?.isComplete
      ) {
        setIsImmigrationPromptOpen(true);
      }
    }

    // Reset selection state after picking
    setSelectedDocumentType(null);
  }, [
    selectedDocumentType,
    getDocumentCategoryId,
    pickFiles,
    uploadDocuments,
    documentGroup,
    householdMemberId,
    householdMember?.citizenshipStatus,
    currentSection?.isComplete,
    memberRequirements,
    requirements,
  ]);

  const handleSaveSsn = ssnForm.handleSubmit(formData => {
    updateHouseholdMember(
      {
        id: householdMemberId,
        householdMemberData: { ssn: formData.ssn },
      },
      {
        onSuccess: () => {
          ssnForm.reset({ ssn: formData.ssn, medicaidSubscriberId: "" });
          sheetToast.success(t("native:applicantDocuments.ssn.saved"));
        },
        onError: () => {
          sheetToast.error(t("native:applicantDocuments.ssn.saveError"));
        },
      },
    );
  });

  const handleUploadPress = () => {
    setIsTypeDialogOpen(true);
  };

  const handleDelete = () => {
    if (documentIdToDelete) {
      deleteDocument(documentIdToDelete, {
        onSuccess: () => {
          setDocumentIdToDelete(null);
          sheetToast.success(
            t("native:applicantDocuments.deleteToast.success"),
          );
        },
        onError: () => {
          setDocumentIdToDelete(null);
          sheetToast.error(t("native:applicantDocuments.deleteToast.error"));
        },
      });
    }
  };

  return (
    <>
      {documentGroup === SSN_GROUP && (
        <VnForm form={ssnForm} isInputOnlyForm>
          <VnBox className="gap-3 pb-2">
            <VnTextInput
              name="ssn"
              control={ssnForm.control}
              labelI18nKey="native:applicantDocuments.ssn.label"
              mask={{ maskGenerator: ssnMask }}
              isRequired={false}
              inputClassName="bg-secondary"
              keyboardType="numeric"
              secureTextEntry={!isSsnVisible}
              isPasswordField={false}
              rightElement={
                <SecureTextEntryToggle
                  isVisible={isSsnVisible}
                  onPress={() => setIsSsnVisible(prev => !prev)}
                />
              }
            />
            <VnButton
              i18nKey="native:applicantDocuments.ssn.save"
              onPress={() => {
                void handleSaveSsn();
              }}
              isLoading={isSavingSsn}
              disabled={
                !ssnForm.formState.isValid || !ssnForm.formState.isDirty
              }
            />
            <VnText
              className="text-muted-foreground text-center"
              i18nKey="native:applicantDocuments.ssn.cardOption"
            />
          </VnBox>
        </VnForm>
      )}

      <DocumentTypeHints
        documentGroup={documentGroup}
        documentTypeOptions={documentTypeOptions}
      />

      <VnSheetToastContainer
        toasts={sheetToast.toasts}
        onDismiss={id => sheetToast.dismissToast(id)}
      />
      <VnBox className="flex flex-1 gap-5 pt-5">
        <UploadButton onPress={handleUploadPress} isUploading={isUploading} />
        {isLoading &&
          !pendingUploads.length &&
          Array.from({ length: 3 }).map((_, index) => (
            <DocumentCardLoadingSkeleton key={index} />
          ))}
        <VnScrollView showsVerticalScrollIndicator={false}>
          {pendingUploads?.map(upload => (
            <DocumentCard
              key={upload.id}
              documentType={upload.documentType}
              status={upload.status}
              updatedAt={upload.updatedAt}
              onDelete={
                upload.documentId
                  ? () => {
                      setDocumentIdToDelete(upload.documentId!);
                    }
                  : undefined
              }
            />
          ))}
          {documents?.map(document => (
            <DocumentCard
              key={document.id}
              documentType={document.category.type}
              status="UPLOADED"
              updatedAt={document.updatedAt}
              onDelete={() => setDocumentIdToDelete(document.id)}
            />
          ))}
        </VnScrollView>
      </VnBox>

      <DocumentTypeSelectDialog
        isOpen={isTypeDialogOpen}
        onOpenChange={setIsTypeDialogOpen}
        documentTypeOptions={documentTypeOptions}
        selectedDocumentType={selectedDocumentType}
        onSelectDocumentType={setSelectedDocumentType}
        onNext={() => {
          void handleDocumentTypeSelect();
        }}
      />

      <VnAlertDialog
        open={Boolean(documentIdToDelete)}
        onOpenChange={isOpen => {
          if (!isOpen) {
            setDocumentIdToDelete(null);
          }
        }}>
        <VnAlertDialogContent>
          <VnAlertDialogHeader>
            <VnAlertDialogTitle i18nKey="native:applicantDocuments.deleteDialog.title" />
            <VnAlertDialogDescription i18nKey="native:applicantDocuments.deleteDialog.description" />
          </VnAlertDialogHeader>
          <VnAlertDialogFooter>
            <VnAlertDialogCancel i18nKey="common:cancel" />
            <VnAlertDialogAction
              variant="destructive"
              i18nKey="common:delete"
              isLoading={isDeletingDocument}
              onPress={() => {
                handleDelete();
              }}
            />
          </VnAlertDialogFooter>
        </VnAlertDialogContent>
      </VnAlertDialog>

      <VnAlertDialog
        open={isImmigrationPromptOpen}
        onOpenChange={setIsImmigrationPromptOpen}>
        <VnAlertDialogContent>
          <VnAlertDialogHeader>
            <VnAlertDialogTitle i18nKey="native:applicantDocuments.immigrationIncomplete.title" />
            <VnAlertDialogDescription
              i18nKey="native:applicantDocuments.immigrationIncomplete.description"
              i18nOptions={{ section: t(labelForDocumentGroup(documentGroup)) }}
            />
          </VnAlertDialogHeader>
          <VnAlertDialogFooter>
            <VnAlertDialogAction
              i18nKey="native:applicantDocuments.immigrationIncomplete.dismiss"
              onPress={() => setIsImmigrationPromptOpen(false)}
            />
          </VnAlertDialogFooter>
        </VnAlertDialogContent>
      </VnAlertDialog>
    </>
  );
};
